import { forwardRef, useImperativeHandle, useState, useEffect, useRef } from 'react';
import Modal from 'components/Modal';
import styled from 'styled-components';
import Checkbox from 'components/Checkbox';
import useGetListMailCPA from 'features/settingService/services/hooks/useGetListMailCPA';
import { Form } from 'antd';
import Text from 'components/Text';
import AddNewEmailCpaInput from './AddNewEmailCpaInput';
import Icon from 'assets/Icons';
import { IApiSendReportEmailsCpaParam } from 'features/report/services/types/api';
import reportApis from 'features/report/services/apis';
import toast from 'utils/toast';
import storage from 'utils/sessionStorage';
import { useNavigate } from 'react-router-dom';

type FormType = {
  emails: { email: string; selected: boolean; newEmail?: boolean }[];
};

export type DataSendEmailCpaModalOpenType = {
  startDate?: string;
  endDate?: string;
  payrollSelectedRowKeys?: React.Key[];
};
export interface ISendEmailCpaModalRef {
  open: (data: DataSendEmailCpaModalOpenType, callback?: () => void) => void;
}

interface Props {}

const SendEmailCpaModal = forwardRef<ISendEmailCpaModalRef, Props>(({}, ref) => {
  const [form] = Form.useForm<FormType>();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [listMailCPA] = useGetListMailCPA();
  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>({ startDate: '', endDate: '' });
  const [payrollSelectedRowKeys, setPayrollSelectedRowKeys] = useState<React.Key[]>([]);
  const [loading, setLoading] = useState(false);

  const refCallback = useRef<() => void>();

  useImperativeHandle(ref, () => ({
    open: (data: DataSendEmailCpaModalOpenType, callback?: () => void) => {
      setVisible(true);
      setDateRange({ startDate: data.startDate || '', endDate: data.endDate || '' });
      setPayrollSelectedRowKeys(data.payrollSelectedRowKeys || []);
      refCallback.current = callback;
    },
  }));

  useEffect(() => {
    if (visible && listMailCPA) {
      const emails = listMailCPA.map((email) => ({ email, selected: true, newEmail: false }));
      form.setFieldsValue({ emails });
    }
  }, [visible, listMailCPA]);

  const handleClose = () => {
    setVisible(false);
    setDateRange({ startDate: '', endDate: '' });
    form.resetFields();
  };

  const handleOk = () => {
    form.submit();
  };

  const handleSetting = () => {
    // navigate to setting page
    navigate(`/store/${storage.shop_id.get()}/setting-service/payments?menuKey=send_email_cpa`);
  };

  const handleFinish = async (values: FormType) => {
    try {
      setLoading(true);
      const body: IApiSendReportEmailsCpaParam = {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        emails: values.emails?.filter((email) => email.selected)?.map((email) => email.email) || [],
        staffIds: payrollSelectedRowKeys.map((key) => key.toString()),
      };
      const res = await reportApis.sendReportEmailsCpa(body);
      if (res.data.data) {
        toast.success('Email sent successfully');
        refCallback.current?.();
        handleClose();
      }
    } catch (error) {
      toast.error('Failed to send email');
    } finally {
      setLoading(false);
    }
  };

  const footer = (
    <FooterContainer>
      <FooterButton className='btn-close' type='button' onClick={handleClose}>
        <div className='text'>Close</div>
      </FooterButton>
      <FooterButton className='btn-setting' type='button' onClick={handleSetting} disabled={loading}>
        <div className='content'>
          <div className='icon'>
            <Icon type='settingWhite' />
          </div>
          <div className='text'>SETTING</div>
        </div>
      </FooterButton>
      <FooterButton className='btn-ok' type='button' onClick={handleOk} disabled={loading}>
        <div className='text'>OK</div>
      </FooterButton>
    </FooterContainer>
  );

  return (
    <Modal
      visible={visible}
      onClose={handleClose}
      modalTitle='SEND EMAIL CPA'
      footer={footer}
      width={600}
      destroyOnClose
      v2={true}
      noneBodyStyle
      noneFooterStyle
    >
      <Container>
        <Form form={form} layout='vertical' onFinish={handleFinish}>
          <div className='email-list-container'>
            <Text variant='H6'>CPA Email List</Text>
            <EmailList>
              <Form.List name='emails'>
                {/* {(fields, { add }) => ( */}
                {(fields) => (
                  <FormListContainer>
                    {fields.map(({ key, name }) => {
                      const emailValue = form.getFieldValue(['emails', name, 'email']);
                      const isNewEmail = form.getFieldValue(['emails', name, 'newEmail']);
                      return (
                        <EmailItem key={key}>
                          <Form.Item name={[name, 'selected']} noStyle>
                            <Checkbox round={true} label={isNewEmail ? '' : emailValue} size='large' customSize={40} />
                          </Form.Item>
                          {isNewEmail && (
                            <Form.Item
                              name={[name, 'email']}
                              style={{ flex: 1 }}
                              rules={[
                                { required: true, message: 'Please enter email!' },
                                { type: 'email', message: 'Please enter valid email!' },
                              ]}
                            >
                              <AddNewEmailCpaInput />
                            </Form.Item>
                          )}
                        </EmailItem>
                      );
                    })}

                    {/* <AddEmailButton type='button' onClick={() => add({ newEmail: true, selected: true })}>
                      <div className='content'>
                        <div className='icon'>
                          <IcAdd />
                        </div>
                        <div className='text'>Add Email</div>
                      </div>
                    </AddEmailButton> */}
                  </FormListContainer>
                )}
              </Form.List>
            </EmailList>
          </div>
        </Form>
      </Container>
    </Modal>
  );
});

SendEmailCpaModal.displayName = 'SendEmailCpaModal';
export default SendEmailCpaModal;

const Container = styled.div`
  padding: 24px 24px 40px;
  .email-list-container {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
`;

const EmailList = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 400px;
  overflow-y: auto;
`;
const FormListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const EmailItem = styled.div`
  display: flex;
  align-items: center;
`;

const FooterContainer = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  width: 100%;
`;

const FooterButton = styled.button`
  display: flex;
  padding: 12px 24px;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  font-family: Poppins;
  font-size: 16px;
  font-weight: 600;
  transition: all 0.3s ease;
  min-width: 100px;
  flex: 1;

  .content {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
  }
  .text {
    color: #fff;
    text-align: center;
    font-family: Poppins;
    font-size: 20px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
  }

  &:hover {
    opacity: 0.8;
    cursor: pointer;
  }

  &.btn-close {
    background: #e5e6eb;
    box-shadow: 0 2px 4px 0 rgba(44, 35, 66, 0.4), 0 4px 8px -3px rgba(45, 35, 66, 0.3), 0 -3px 0 0 #d6d6e7 inset;
    .text {
      color: #1d2129;
    }
  }

  &.btn-setting {
    background: #6fabb6;
    box-shadow: 0 2px 4px 0 rgba(44, 35, 66, 0.4), 0 4px 8px -3px rgba(45, 35, 66, 0.3), 0 -4px 0 0 #144b55 inset;
  }

  &.btn-ok {
    background: #ff8890;
    box-shadow: 0 2px 4px 0 rgba(44, 35, 66, 0.4), 0 4px 8px -3px rgba(45, 35, 66, 0.3), 0 -4px 0 0 #e7646d inset;
  }
`;

// const AddEmailButton = styled.button`
//   display: flex;
//   height: 48px;
//   padding: 16px 24px;
//   flex-direction: column;
//   justify-content: center;
//   align-items: center;
//   gap: 4px;
//   transition: all 0.3s ease;
//   &:hover {
//     opacity: 0.8;
//     cursor: pointer;
//   }
//   .content {
//     display: flex;
//     align-items: center;
//     gap: 8px;
//   }
//   .icon {
//     display: flex;
//     align-items: center;
//     justify-content: center;
//     width: 24px;
//     height: 24px;
//   }

//   .text {
//     color: #2d6772;
//     text-align: center;
//     font-family: Poppins;
//     font-size: 16px;
//     font-style: normal;
//     font-weight: 600;
//     line-height: normal;
//   }
//   border-radius: 4px;
//   border: 1px solid #2d6772;
//   background: #fff;
//   box-shadow: 0 2px 4px 0 rgba(44, 35, 66, 0.4), 0 4px 8px -3px rgba(45, 35, 66, 0.3), 0 -3px 0 0 #144b55 inset;
// `;

// const IcAdd = () => (
//   <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none'>
//     <path
//       fillRule='evenodd'
//       clipRule='evenodd'
//       d='M12 4.5C12.4142 4.5 12.75 4.83579 12.75 5.25V18.75C12.75 19.1642 12.4142 19.5 12 19.5C11.5858 19.5 11.25 19.1642 11.25 18.75V5.25C11.25 4.83579 11.5858 4.5 12 4.5Z'
//       fill='#2D6772'
//     />
//     <path
//       fillRule='evenodd'
//       clipRule='evenodd'
//       d='M4.5 12C4.5 11.5858 4.83579 11.25 5.25 11.25H18.75C19.1642 11.25 19.5 11.5858 19.5 12C19.5 12.4142 19.1642 12.75 18.75 12.75H5.25C4.83579 12.75 4.5 12.4142 4.5 12Z'
//       fill='#2D6772'
//     />
//     <path
//       fillRule='evenodd'
//       clipRule='evenodd'
//       d='M12 4C11.3096 4 10.75 4.55964 10.75 5.25V10.75H5.25C4.55964 10.75 4 11.3096 4 12C4 12.6904 4.55964 13.25 5.25 13.25H10.75V18.75C10.75 19.4404 11.3096 20 12 20C12.6904 20 13.25 19.4404 13.25 18.75V13.25H18.75C19.4404 13.25 20 12.6904 20 12C20 11.3096 19.4404 10.75 18.75 10.75H13.25V5.25C13.25 4.55964 12.6904 4 12 4ZM12.25 10.75V5.25C12.25 5.11193 12.1381 5 12 5C11.8619 5 11.75 5.11193 11.75 5.25V10.75H12.25ZM11.75 11.75H12.25V12.25H11.75V11.75ZM10.75 11.75H5.25C5.11193 11.75 5 11.8619 5 12C5 12.1381 5.11193 12.25 5.25 12.25H10.75V11.75ZM11.75 13.25V18.75C11.75 18.8881 11.8619 19 12 19C12.1381 19 12.25 18.8881 12.25 18.75V13.25H11.75ZM13.25 12.25V11.75H18.75C18.8881 11.75 19 11.8619 19 12C19 12.1381 18.8881 12.25 18.75 12.25H13.25Z'
//       fill='#2D6772'
//     />
//   </svg>
// );

// const SubContentForm = styled.div`
//   display: flex;
//   flex-direction: column;
//   gap: 16px;
// `;
